import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { findSmiley, searchSmileys, SMILEYS, smileyUrl } from '../lib/smileys.ts';
import { useSmileyStats } from '../lib/smileyStats.ts';
import './SmileyPicker.css';

type Category = 'Часто' | 'Все';

const CATEGORIES: Category[] = ['Часто', 'Все'];

/**
 * The grid picker that floats above the composer.
 *
 * It stays open after a pick — the whole point is putting three of them in a
 * row — so closing is always something the reader asks for: the ✕, the toggle
 * button, or Escape.
 */
export function SmileyPicker({
  onPick,
  onClose,
}: {
  onPick: (slug: string) => void;
  onClose: () => void;
}) {
  const [category, setCategory] = useState<Category>('Часто');
  const [query, setQuery] = useState('');
  const [hovered, setHovered] = useState('smile');
  const frequent = useSmileyStats();

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // A search runs across the whole set, overriding the category tabs.
  const searching = query.trim().length > 0;
  const shown = searching ? searchSmileys(query) : category === 'Часто' ? frequent : SMILEYS;
  const preview = findSmiley(hovered) ?? SMILEYS[0]!;

  return (
    <div className="smiley-panel" role="dialog" aria-label="Смайлы">
      <div className="smiley-head">
        <input
          className="smiley-search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Поиск смайла"
          aria-label="Поиск смайла"
        />
        <button type="button" className="smiley-close" onClick={onClose} aria-label="Закрыть">
          <X size={14} />
        </button>
      </div>

      <div className="smiley-tabs">
        {CATEGORIES.map((name) => (
          <button
            key={name}
            type="button"
            className={`smiley-tab${!searching && category === name ? ' smiley-tab-active' : ''}`}
            aria-pressed={!searching && category === name}
            onClick={() => {
              setCategory(name);
              setQuery('');
            }}
          >
            {name}
          </button>
        ))}
      </div>

      <div className="smiley-grid">
        {shown.map((smiley) => (
          <button
            key={smiley.slug}
            type="button"
            className="smiley-cell"
            title={smiley.label}
            aria-label={smiley.label}
            // The composer keeps the caret: losing it here would insert at the
            // end of the message instead of where the reader was writing.
            onMouseDown={(event) => event.preventDefault()}
            onMouseEnter={() => setHovered(smiley.slug)}
            onFocus={() => setHovered(smiley.slug)}
            onClick={() => onPick(smiley.slug)}
          >
            <img src={smileyUrl(smiley.slug)} alt="" loading="lazy" />
          </button>
        ))}
        {shown.length === 0 && <div className="smiley-empty">Ничего не найдено</div>}
      </div>

      <div className="smiley-foot">
        <img className="smiley-foot-image" src={smileyUrl(preview.slug)} alt="" />
        <div className="smiley-foot-text">
          <div className="smiley-foot-name">{preview.label}</div>
          <code className="smiley-foot-code">:{preview.slug}:</code>
        </div>
        <div className="smiley-foot-count">
          {shown.length} из {SMILEYS.length}
        </div>
      </div>
    </div>
  );
}
